import * as Sentry from '@sentry/react';
import { clearFailedChunks, isChunkLoadError, refreshLazyComponents } from 'app/utils/chunkLoadErrorHandler';
import ButtonComponent from 'core/modules/atoms/Button';
import IconComponent from 'core/modules/atoms/Icon';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

// Type assertions for JS components without TypeScript definitions
const Button = ButtonComponent as React.ComponentType<{
  text?: string;
  icon?: string;
  color?: string;
  size?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
}>;

const Icon = IconComponent as React.ComponentType<{
  type?: string;
  size?: string;
  color?: string;
}>;

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRetrying: boolean;
  retryKey: number;
}

/**
 * Error boundary that catches chunk loading failures (CSS/JS)
 * and provides user-friendly recovery options.
 *
 * Uses inline styles for container as fallback in case CSS chunks also failed,
 * but leverages core Button and Icon atoms for consistent UI.
 */
class ChunkLoadErrorBoundary extends Component<Props, State> {
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isRetrying: false,
      retryKey: 0,
    };
  }

  componentWillUnmount(): void {
    if (this.retryTimeout !== null) {
      clearTimeout(this.retryTimeout);
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, isRetrying: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    // Report to Sentry with context
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'chunk_load_error');
      scope.setTag('route', window.location.pathname);
      scope.setExtra('componentStack', errorInfo.componentStack);
      scope.setExtra('url', window.location.href);
      scope.setExtra('userAgent', navigator.userAgent);
      scope.setLevel('error');
      Sentry.captureException(error);
    });

    console.error('[ChunkLoadErrorBoundary] Chunk load error:', {
      message: error.message,
      route: window.location.pathname,
      stack: errorInfo.componentStack,
    });
  }

  handleRetry = (): void => {
    this.setState({ isRetrying: true });

    // 1. Clear failed chunk cache in bundler runtime
    clearFailedChunks();

    // 2. Refresh lazy components to create new instances
    // This is the key step - it forces lazyWithRetry wrappers to create fresh React.lazy components
    refreshLazyComponents();

    // Small delay to show loading state, then reset error state
    this.retryTimeout = setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        isRetrying: false,
        retryKey: this.state.retryKey + 1,
      });
    }, 500);
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, isRetrying, retryKey } = this.state;
    const { children, fallback, t } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      // Inline styles as fallback in case CSS chunk also failed
      const containerStyle: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 99999,
      };

      const contentStyle: React.CSSProperties = {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '400px',
        textAlign: 'center',
      };

      const titleStyle: React.CSSProperties = {
        margin: '16px 0 8px',
        fontSize: '20px',
        fontWeight: 600,
        color: '#333',
      };

      const messageStyle: React.CSSProperties = {
        margin: '0 0 24px',
        fontSize: '14px',
        color: '#666',
        lineHeight: 1.5,
      };

      const actionsStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      };

      return (
        <div className="chunk-error" style={containerStyle}>
          <div className="chunk-error__content" style={contentStyle}>
            <div className="chunk-error__icon">
              <Icon type="warning-circle-fill" size="xxl" color="primary" />
            </div>

            <h2 className="chunk-error__title" style={titleStyle}>
              {t('common:Error loading page')}
            </h2>

            <p className="chunk-error__message" style={messageStyle}>
              {t('common:Page load error message')}
            </p>

            <div className="chunk-error__actions" style={actionsStyle}>
              <Button
                text={isRetrying ? t('common:Retrying') : t('common:Try again')}
                color="primary"
                size="medium"
                onClick={this.handleRetry}
                disabled={isRetrying}
                loading={isRetrying}
                icon="refresh"
              />

              <Button
                text={t('common:Reload page')}
                color="grey"
                size="medium"
                onClick={this.handleReload}
                disabled={isRetrying}
              />
            </div>
          </div>
        </div>
      );
    }

    // Use key to force remount of children (and React.lazy components) on retry
    return <React.Fragment key={retryKey}>{children}</React.Fragment>;
  }
}

export default withTranslation()(ChunkLoadErrorBoundary);